import React, { useMemo } from "react";
import KC_Card from "../../../../designsystem/KC_Card/KC_Card";
import {
  type ActiveEvent,
  type Config,
  EventConfig,
  GameTypes
} from "kadow-jetpack-model";
import KC_Button, {
  KC_ButtonSize
} from "../../../../designsystem/KC_Button/KC_Button";
import { isPerformanceGameHiddenByDisabledMonthlyLottery } from "../../../../utils/helpers/performanceGameAvailability";
import { useEventsSelector } from "../../../../redux/actions/wheel";
import "./styles.scss";
import KC_UIText from "../../../../designsystem/typography/KC_UIText/KC_UIText";
import TSUtils from "../../../../utils/helpers/TSUtils";
import {
  updateJetpackConfigThunk,
  useJetpackConfigSelector
} from "../../../../redux/jetpackConfig";
import { useDispatch } from "react-redux";
import { useAccountToDisplaySelector } from "../../../../redux/actions/user";
import GameCardPreview from "../../../23_GameCardPreview/GameCardPreview";
import { LANGUAGE, tt } from "../../../../utils/i18n/i18n";
import { getDisplayNameForGameType } from "../../../../utils/helpers/getDisplayNameForGameType";
import Dot from "../../../../components/Dot/Dot";
import moment from "moment-timezone";

interface EventSectionPropsI {
  activeEvents: ActiveEvent[];
}

export default function EventSection(props: EventSectionPropsI) {
  const events = useEventsSelector();
  const dispatch = useDispatch();
  const { accountToDisplay } = useAccountToDisplaySelector();
  const config = useJetpackConfigSelector();

  const dispatchUpdate = (update: Partial<Config>) => {
    dispatch(
      updateJetpackConfigThunk(
        update,
        accountToDisplay?.trackingID,
        accountToDisplay?.token
      )
    );
  };

  const handleDelete = (ae: ActiveEvent) => {
    if (!config?.activeEvents?.length) {
      return;
    }

    if (!confirm(tt("confirm_deletion"))) {
      return;
    }

    const idx = config.activeEvents.findIndex(
      (configAE) =>
        configAE.id === ae.id &&
        configAE.sts === ae.sts &&
        configAE.ets === ae.ets &&
        configAE.gameType === ae.gameType
    );

    if (idx === -1) {
      return;
    }

    const newActiveEvents = [...config.activeEvents];
    const [removed] = newActiveEvents.splice(idx, 1);

    /*
      Scheduled events are re-added by the batch, so instead of dropping them we move
      them to config.dse (disabled scheduled events). The batch skips anything listed
      there, and the customer can restore it. Manual events are simply removed.
    */
    if (ae.s) {
      dispatchUpdate({
        activeEvents: newActiveEvents,
        dse: [...(config.dse || []), removed]
      });
    } else {
      dispatchUpdate({ activeEvents: newActiveEvents });
    }
  };

  const handleRestore = (ae: ActiveEvent) => {
    if (!config?.dse?.length) {
      return;
    }

    const idx = config.dse.findIndex(
      (e) =>
        e.id === ae.id &&
        e.sts === ae.sts &&
        e.ets === ae.ets &&
        e.gameType === ae.gameType
    );

    if (idx === -1) {
      return;
    }

    const newDse = [...config.dse];
    const [restored] = newDse.splice(idx, 1);

    dispatchUpdate({
      activeEvents: [...(config.activeEvents || []), restored],
      dse: newDse
    });
  };

  /*
    Disabled scheduled events (config.dse) stay in the list, flagged as disabled so
    they render dimmed with a Restore button instead of a Delete button.
  */
  const eventsToDisplay = useMemo(() => {
    const actives = (props.activeEvents || []).map((ae) => ({
      ae,
      disabled: false
    }));
    const disabled = (config?.dse || []).map((ae) => ({ ae, disabled: true }));
    return [...actives, ...disabled].sort((a, b) =>
      TSUtils.sortNumbers(a.ae, b.ae, "sts", "asc")
    );
  }, [props.activeEvents, config?.dse]);

  const NOW_TS = useMemo(() => {
    return moment().unix();
  }, []);

  const GAMES = useMemo(() => {
    return eventsToDisplay
      .map(({ ae, disabled }, idx) => {
        if (!ae.gameType) {
          return null;
        }

        const eventConfig = events?.find((e) => e._id === ae.id);

        if (!eventConfig) {
          return null;
        }

        const skin = eventConfig.skins.find((s) => s.game === ae.gameType);

        if (!skin) {
          return null;
        }

        const isOngoing =
          !!eventConfig.permanent || (NOW_TS >= ae.sts && NOW_TS <= ae.ets);

        const gt = ae.gameType as unknown as GameTypes;

        if (gt && isPerformanceGameHiddenByDisabledMonthlyLottery(gt, config)) {
          return null;
        }

        return (
          <KC_Card
            key={`${ae.id}-${idx}`}
            title={getDisplayNameForGameType(ae.gameType)}
            boxShadow={false}
            description=""
            useTwicPics={true}
            isGameCardPreview={true}
            className={disabled ? "border kc-event-disabled" : "border"}
            canDelete={!disabled}
            isSelected={!disabled}
            handleDelete={() => handleDelete(ae)}
            children={
              <>
                <GameCardPreview
                  gameCardConfig={skin.gameCardConfig}
                  eventId={ae.id}
                />
                <div className="d-flex align-items-center">
                  <Dot
                    color={
                      !disabled && (!!eventConfig.permanent || isOngoing)
                        ? "green"
                        : "red"
                    }
                  />
                  <KC_UIText
                    className="ms-2"
                    weight="reg"
                    text={
                      !!eventConfig.permanent ? (
                        <KC_UIText
                          weight="reg"
                          text={tt("active_permanently")}
                        />
                      ) : (
                        formatStartAndEndDatesForActiveEvent(ae)
                      )
                    }
                  />
                </div>
                {!!ae.s && (
                  <div className="d-flex align-items-center mt-1">
                    <KC_UIText weight="bold" text={tt("scheduled")} />
                    <i
                      className="fas fa-info-circle ml-2"
                      title={tt("scheduled_tooltip")}
                    />
                  </div>
                )}
                {disabled && (
                  <div className="d-flex flex-column mt-1 st-gap">
                    <KC_UIText weight="bold" text={tt("event_disabled")} />
                    <KC_Button
                      size={KC_ButtonSize.Small}
                      isSecondary={true}
                      text={tt("restore")}
                      onClick={() => handleRestore(ae)}
                    />
                  </div>
                )}
              </>
            }
          />
        );
      })
      .filter((s) => !!s);
  }, [config, eventsToDisplay]);

  return (
    <div className="d-flex flex-column st-gap">{GAMES.map((game) => game)}</div>
  );
}

export const getEventName = (event?: EventConfig, gameType?: string) => {
  if (!event || !event.name) {
    if (gameType) {
      return getDisplayNameForGameType(gameType);
    }
    return "";
  }
  return event.nameL?.[LANGUAGE] || event.name;
};

function formatStartAndEndDatesForActiveEvent(ae: ActiveEvent) {
  return (
    <div>
      {tt("event_display_from")}{" "}
      <KC_UIText weight="bold" text={TSUtils.formatDate(ae.sts)} />{" "}
      {tt("event_display_to")}{" "}
      <KC_UIText weight="bold" text={TSUtils.formatDate(ae.ets)} />
    </div>
  );
}
